import {createRouter, createWebHistory, type RouteLocationNormalized} from 'vue-router'

import type {Store} from 'vuex';
import {store} from '@/store';
import {constantRoutes} from '@/router/routes';
import type {RootState} from '@/types/RootState';
import companyStore from '@/middleware/companyStore';
import locale from '@/middleware/locale';
import checkAuth from '@/middleware/check-auth';
import auth from '@/middleware/auth';
import guest from '@/middleware/guest';
import superAdmin from '@/middleware/superAdmin';
import {d} from '@/utils/consoleHelper.ts';
import {clearNavigationData, getNavigationData} from '@/composables/useNavigationHelper';
import {createHashScrollBehavior} from '@/composables/useHashScroll';
import {handleStaleAssetError} from '@/utils/staleAssetReload';

export type MiddlewareContext = {
    store: Store<RootState>;
    to: RouteLocationNormalized;
    from: RouteLocationNormalized;
};

export type MiddlewareFn = (_context: MiddlewareContext) => Promise<boolean | void>;

const globalMiddleware: Array<string | MiddlewareFn> = [];

const router = createRouter({
    history: createWebHistory(),
    routes: constantRoutes,
    scrollBehavior: createHashScrollBehavior({attempts: 12}),
})


router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
    d.log('%c       START BEFORE EACH IN ROUTER   \\/  ', 'color: black;background: yellow; padding: 2px;border-top: 1px solid red;');
    d.dir(to);

    if (store.state.app.update_available) {
        window.location.reload();
    }

    // Clear navigation return data when navigating away from settings or to the dashboard
    const navigationData = getNavigationData();
    if (navigationData) {
        const isNavigatingToDashboard = to.name === 'dashboard';

        // Extract the settings section from the target path
        // We need to determine which settings section the target belongs to
        // e.g., /settings/taxes -> settings.taxes section
        //       /settings/pricing-structure -> settings.pricing.template section
        let targetSettingsSection: string | null = null;
        if (navigationData.target) {
            const targetPath = navigationData.target.split('#')[0]; // Remove hash

            if (targetPath.startsWith('/settings/')) {
                const pathAfterSettings = targetPath.replace('/settings/', '');
                const firstSegment = pathAfterSettings.split('/')[0];

                // Map path segments to route name patterns
                // This determines which settings section we're in
                if (firstSegment === 'taxes') {
                    targetSettingsSection = 'settings.taxes';
                } else if (firstSegment === 'pricing-structure') {
                    targetSettingsSection = 'settings.pricing.template';
                } else if (firstSegment === 'pricing') {
                    targetSettingsSection = 'settings.pricing';
                } else if (firstSegment === 'general') {
                    targetSettingsSection = 'settings.general';
                } else if (firstSegment === 'security-deposits') {
                    targetSettingsSection = 'settings.pricing.deposits';
                } else if (firstSegment === 'fleet') {
                    targetSettingsSection = 'settings.fleet';
                } else if (firstSegment === 'account') {
                    targetSettingsSection = 'settings.account';
                } else {
                    // Fallback: convert a kebab-case to route a name pattern
                    // e.g., "custom-fields" -> "settings.custom-fields"
                    targetSettingsSection = `settings.${firstSegment}`;
                }
            }
        }

        // Check if we're still in the same settings section
        // e.g., if the target is settings.taxes, keep for settings.taxes.new, settings.taxes.edit, etc.
        const isInSameSettingsSection = targetSettingsSection && to.name?.toString().startsWith(targetSettingsSection);

        const isOnSettingsPage = to.name?.toString().startsWith('settings.');
        const wasOnSettingsPage = from.name?.toString().startsWith('settings.');
        const isLeavingSettings = wasOnSettingsPage && !isOnSettingsPage;

        // Clear if:
        // 1. Navigating to dashboard
        // 2. Navigating away from settings pages (was on settings, now not)
        // 3. Navigating to a different settings section (not in the same section as target)
        // Note: We do NOT clear when:
        //   - Arriving at the target page
        //   - Navigating within the same settings section (e.g., taxes -> taxes.new)
        const isNavigatingToDifferentSection = isOnSettingsPage && targetSettingsSection && !isInSameSettingsSection;

        if (isNavigatingToDashboard || isLeavingSettings || isNavigatingToDifferentSection) {
            clearNavigationData();
        }
    }

    const context: { store: Store<RootState>; to: RouteLocationNormalized; from: RouteLocationNormalized } = {
        to,
        from,
        store
    };


    // ✅ Ensure checkAuth runs first
    await checkAuth(context).then(() => {
        d.log('%c checkAuth Completed ✅', 'color: green;');
    });

    // ✅ Extract middleware for the route
    const middleware = getMiddleware(to.matched);


    if (!middleware.length) {
        d.log('%c NO MIDDLEWARE ❌', 'color: red;');
        return; // No middleware, continue navigation
    }


    const middlewareNames = middleware.map((middleware) => {
        return typeof middleware === 'function' ? middleware.name : middleware
    });

    d.log('%c Middleware Found ✅', 'color: white;background: pink; padding: 2px; font-weight: bold; padding: 5px;');
    d.table(middlewareNames);

    // ✅ Ensure locale middleware always runs first
    await locale();

    // COMPANY STORE MIDDLEWARE
    if (middlewareNames.includes('companyStore')) {

        await companyStore(context);

    }


    // GUEST MIDDLEWARE
    if (middlewareNames.includes('guest')) {

        if (!(await guest(context))) {
            const incomplete = store.getters['auth/user']?.registration_incomplete;
            d.log('%c REDIRECT TO DASH', 'color: white;background: pink; padding: 2px; font-weight: bold; padding: 5px;');
            return {name: incomplete ? 'company.next-step' : 'dashboard'}
        }
    }

    // Incomplete owner signup must finish /company before the dashboard.
    if (
        store.getters['auth/token'] &&
        store.getters['auth/user']?.registration_incomplete &&
        to.name !== 'company.next-step'
    ) {
        const isAppRoute = middlewareNames.includes('auth') || to.name === 'dashboard';
        if (isAppRoute) {
            return {name: 'company.next-step'};
        }
    }

    // AUTH MIDDLEWARE
    if (middlewareNames.includes('auth')) {
        d.dir('middlewareNames.includes(\'auth\')');

        const isAuth = await auth(context);

        if (
            !isAuth &&
            // ❗️ Avoid an infinite redirect
            to.name !== 'login'
        ) {
            // redirect the user to the login page
            return {name: 'login', query: {redirect: to.fullPath}}
        }
    }

    // SUPER ADMIN MIDDLEWARE – block non–super-admin users from super admin routes
    if (middlewareNames.includes('superAdmin')) {
        const hasSuperAdmin = await superAdmin(context);
        if (!hasSuperAdmin) {
            return {name: 'dashboard'};
        }
    }

    d.log('%c       END BEFORE EACH IN ROUTER   /\\  ', 'color: black;background: yellow; padding: 2px;border-bottom: 1px solid red');
});

router.onError((err) => {
    if (handleStaleAssetError(err)) {
        return;
    }

    throw err;
});

export default router;


/**
 * Merge the global middleware with the component's middleware.
 */
function getMiddleware(matchedRoutes: RouteLocationNormalized['matched']): Array<string | MiddlewareFn> {
    const middleware: Array<string | MiddlewareFn> = [...globalMiddleware];

    matchedRoutes.forEach((route) => {
        if (route.meta?.middleware) {
            if (Array.isArray(route.meta.middleware)) {
                middleware.push(...route.meta.middleware as Array<string | MiddlewareFn>);
            } else {
                middleware.push(route.meta.middleware as string | MiddlewareFn);
            }
        }
    });

    // remove duplicates
    return [...new Set(middleware)];
}
